<script setup lang="ts">
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import AuthLayout from '../components/AuthLayout.vue';
import { Button, Input } from '../components/ui';
import { createEventMagicLink } from '../api';
import { signInPath } from '../services/authPaths';
import { isValidEmail } from '../services/validators';

const instance = getCurrentInstance();
const route = instance?.proxy?.$route;

const email = ref('');
const emailError = ref('');
const backendError = ref('');
const loading = ref(false);
const success = ref(false);
const token = ref('');

const topLinks = computed(() => {
  if (success.value) {
    return [{ url: signInPath(email.value), text: 'Back to sign in' }];
  }
  return [{ url: signInPath(), text: 'Do you have an account already?' }];
});

const bottomLinks = computed(() => []);

async function handleSubmit() {
  emailError.value = '';
  backendError.value = '';

  if (!isValidEmail(email.value)) {
    emailError.value = 'Please enter a valid email.';
    return;
  }

  if (!token.value) {
    backendError.value = 'Invalid token or event';
    return;
  }

  loading.value = true;

  try {
    await createEventMagicLink(email.value, token.value);
    success.value = true;
  } catch (error) {
    backendError.value = error instanceof Error ? error.message : 'Invalid token or event';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  const rawToken = route?.query.token;
  if (typeof rawToken === 'string') {
    token.value = rawToken;
  }
});
</script>

<template>
  <AuthLayout title="Welcome" :top-links="topLinks" :bottom-links="bottomLinks">
    <div v-if="success" class="text-white text-left">
      <p class="mb-2">Please check your <strong>email</strong>.</p>
      <p class="text-sm text-white/75">Your token will expire in 10 minutes.</p>
    </div>

    <form v-else @submit.prevent="handleSubmit">
      <p class="text-white mb-2">Access to Spireworks via <strong>email</strong>.</p>
      <p class="text-white/75 text-sm mb-4">We will send you a token for a password-free sign in.</p>

      <Input v-model="email" type="email" placeholder="Email" autocomplete="email" :error="emailError" />

      <p v-if="backendError" class="text-red-400 text-sm mt-2">{{ backendError }}</p>

      <div class="mt-form-gap">
        <Button type="submit" variant="primary" size="lg" :loading="loading" :disabled="loading"> Send </Button>
      </div>
    </form>
  </AuthLayout>
</template>
